"use client";

import { AlertTriangle, Check } from "lucide-react";
import React, { useState } from "react";

import { Badge } from "../../../shadcn/ui/badge";
import { Button } from "../../../shadcn/ui/button";
import { CancelConfirmDialog } from "./CancelConfirmDialog";
import { CreditBalanceModalProps } from "./types";
import { MobileCancelConfirmScreen } from "./MobileCancelConfirmScreen";
import dayjs from "@punchup/utils/dayjs";
import { toast } from "../../../shadcn/ui/use-toast";

export function PaidPlanCard({
  subscription,
  onCancelSubscription,
  onUpdatePaymentMethod,
  onChangePlan,
  onRefill,
  isDesktop,
}: {
  subscription: NonNullable<CreditBalanceModalProps["activeSubscription"]>;
  onCancelSubscription?: (subscriptionId: string) => Promise<void>;
  onUpdatePaymentMethod?: () => Promise<string>;
  onChangePlan?: () => void;
  onRefill?: () => void;
  isDesktop: boolean;
}) {
  const [isCancelling, setIsCancelling] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const creditLabel = `${subscription.creditAmount.toLocaleString()} credits/mo`;
  const creditPerMonth = `${subscription.creditAmount.toLocaleString()} credits/month`;
  const renewalText = subscription.renewalDate
    ? `Renews ${dayjs(subscription.renewalDate).format("MMM D, YYYY")}`
    : null;

  const handleCancelConfirm = async () => {
    if (!onCancelSubscription) return;
    setIsCancelling(true);
    try {
      await onCancelSubscription(subscription.subscriptionId);
      toast({
        title: "Subscription cancelled",
        description:
          "Subscription cancelled. Your remaining credits are still available.",
      });
    } catch {
      toast({
        title: "Error",
        description:
          "Something went wrong cancelling your subscription. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsCancelling(false);
      setShowCancelConfirm(false);
    }
  };

  const handleUpdatePayment = async () => {
    if (!onUpdatePaymentMethod) return;
    try {
      const url = await onUpdatePaymentMethod();
      if (url) window.open(url, "_blank");
    } catch {
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    }
  };

  if (showCancelConfirm && !isDesktop) {
    return (
      <MobileCancelConfirmScreen
        onBack={() => setShowCancelConfirm(false)}
        onConfirm={handleCancelConfirm}
        isCancelling={isCancelling}
      />
    );
  }

  return (
    <>
      <div className="mt-4 w-full sm:max-w-[357px] rounded-[10px] border border-[#A4A6A8] px-4 py-4 space-y-3">
        <Badge className="bg-[#FE5E25]/10 text-[#FE5E25] border-0 hover:bg-[#FE5E25]/10">
          {creditLabel}
        </Badge>
        {renewalText && (
          <p className="mt-1 text-sm font-normal text-[#787B7E]">
            {renewalText}
          </p>
        )}

        {/* Payment failure banner */}
        {subscription.status === "past_due" && (
          <div className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
            <div className="text-sm text-red-600">
              <p>
                Your last payment didn&apos;t go through. Update your payment
                method to keep your subscription active.
              </p>
              <button
                type="button"
                onClick={handleUpdatePayment}
                className="underline text-sm text-red-600 mt-1"
              >
                Update payment method
              </button>
            </div>
          </div>
        )}

        <p className="text-2xl font-semibold text-[#29333C]">
          {subscription.priceFormatted}{" "}
          <span className="text-base font-normal text-[#787B7E]">/ month</span>
        </p>
        <div className="space-y-2">
          {[
            "Free emails to fans who signed up on Punchup",
            creditPerMonth,
            "Credit cost depends on SMS vs. email",
          ].map((item) => (
            <div key={item} className="flex items-center gap-2">
              <Check className="h-4 w-4 text-[#29333C] stroke-[2.5]" />
              <span className="text-sm text-[#29333C]">{item}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Action links row — Refill (orange), Change plan (grey), Cancel (grey) */}
      <div className="mt-3 flex items-center flex-wrap gap-x-3 gap-y-1">
        {onRefill && (
          <button
            type="button"
            onClick={onRefill}
            className="text-sm text-[#FE5E25] underline hover:opacity-80"
          >
            Refill
          </button>
        )}
        {onChangePlan && (
          <button
            type="button"
            onClick={onChangePlan}
            className="text-sm text-[#787B7E] hover:underline"
          >
            Change plan
          </button>
        )}
        {onCancelSubscription && (
          <button
            type="button"
            onClick={() => setShowCancelConfirm(true)}
            disabled={isCancelling}
            className="text-sm text-[#787B7E] hover:underline disabled:opacity-50"
          >
            {isCancelling ? "Cancelling..." : "Cancel subscription"}
          </button>
        )}
      </div>

      {/* Cancellation confirmation (desktop only -- mobile handled above) */}
      {isDesktop && (
        <CancelConfirmDialog
          open={showCancelConfirm}
          onOpenChange={setShowCancelConfirm}
          onConfirm={handleCancelConfirm}
          isCancelling={isCancelling}
        />
      )}
    </>
  );
}
